// Full history of a single hotel BOOKING by id — guest/summary, rooms (with
// change remarks), payments (CR/DR), add-ons, food orders charged to the room.
// Read-only.
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import config from '../../config';

const token = sessionStorage.getItem('token');
const auth = { Authorization: `Bearer ${token}` };
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');
const dOnly = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

function Section({ title, children }) {
  return (
    <Paper elevation={2} sx={{ borderRadius: 3, p: 2, mb: 2 }}>
      <Typography fontWeight={800} sx={{ mb: 1, color: '#0a2540' }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </Paper>
  );
}

function Info(rows) {
  return (
    <Grid container spacing={1}>
      {rows.map(([k, v]) => (
        <Grid item xs={6} md={3} key={k}>
          <Typography variant='caption' color='text.secondary'>
            {k}
          </Typography>
          <Typography fontWeight={600}>{v ?? '—'}</Typography>
        </Grid>
      ))}
    </Grid>
  );
}

export default function BookingHistory() {
  const [bookingId, setBookingId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    const id = Number(bookingId);
    if (!id) return toast.info('Enter a booking id');
    setLoading(true);
    setData(null);
    fetch(`${config.API_BASE_URL}/history/booking/${id}`, { headers: auth })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setData(d))
      .catch(() => toast.error('Booking not found'))
      .finally(() => setLoading(false));
  };

  const b = data?.booking;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          color: '#fff',
          background:
            'linear-gradient(135deg,#0b1120 0%,#0f172a 40%,#1e3a8a 100%)',
        }}>
        <Typography variant='h5' fontWeight={800} sx={{ color: '#fff' }}>
          🛎️ Booking History
        </Typography>
        <Typography variant='body2' sx={{ opacity: 0.85, color: '#fff' }}>
          Enter a booking id to see the full stay — rooms, payments, add-ons & food
        </Typography>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 3, p: 2, mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size='small'
          type='number'
          label='Booking ID'
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          sx={{ minWidth: 200 }}
        />
        <Button variant='contained' startIcon={<SearchIcon />} onClick={load}>
          Load History
        </Button>
      </Paper>

      {loading && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {b && (
        <>
          <Section title={`Booking #${b.id} — ${b.guestName || 'Guest'}`}>
            {Info([
              ['Mobile', b.mobileNumber],
              ['Email', b.email],
              ['City', b.city],
              ['Payment Mode', b.paymentMode],
              ['Check-In', dOnly(b.checkIn)],
              ['Check-Out', dOnly(b.checkOut)],
              ['Room Price', fmt(b.price)],
              ['Final (incl GST)', fmt(b.finalpriceWithGST)],
              ['Discount', `${b.discountType || ''} ${b.discountValue || ''}`.trim() || '—'],
              ['Company', b.companyname || '—'],
              ['GST No', b.gstNo || '—'],
              ['Created', dt(b.createdAt)],
            ])}
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size='small' color={b.ischeckin ? 'success' : 'default'} label={b.ischeckin ? 'Checked-in' : 'Not checked-in'} />
              <Chip size='small' color={b.isinvoice ? 'info' : 'default'} label={b.isinvoice ? 'Invoiced' : 'No invoice'} />
              {b.isCancel && <Chip size='small' color='error' label='Cancelled' />}
            </Box>
          </Section>

          <Section title={`Rooms (${data.rooms?.length || 0})`}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Room No', 'Active', 'Change Remark', 'When'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.rooms || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontWeight: 700 }}>{r.roomnumber}</TableCell>
                    <TableCell>
                      <Chip size='small' color={r.isActive ? 'success' : 'default'} label={r.isActive ? 'Yes' : 'No'} />
                    </TableCell>
                    <TableCell>{r.changeRemark || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(r.created)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>

          <Section title={`Payments — CR/DR  (CR ${fmt(data.totals?.totalCr)} · DR ${fmt(data.totals?.totalDr)} · Net ${fmt(data.totals?.balance)})`}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Date', 'Type', 'Amount', 'Mode', 'Ref/Card', 'Remark', 'By'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.payments || []).length === 0 ? (
                  <TableRow><TableCell colSpan={7} align='center' sx={{ color: '#94a3b8' }}>No payments.</TableCell></TableRow>
                ) : (
                  data.payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(p.date)}</TableCell>
                      <TableCell>
                        <Chip size='small' color={p.type === 'CR' ? 'success' : 'warning'} label={p.type || '—'} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{fmt(p.amount)}</TableCell>
                      <TableCell>{p.mode || '—'}</TableCell>
                      <TableCell>{[p.refNo, p.cardType].filter(Boolean).join(' / ') || '—'}</TableCell>
                      <TableCell>{p.remark || '—'}</TableCell>
                      <TableCell>{p.userName || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Section>

          {(data.addons || []).length > 0 && (
            <Section title={`Add-ons (${data.addons.length})`}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {['Name', 'Qty', 'Unit Price', 'GST %', 'Active', 'Added'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.addons.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell>{a.name}</TableCell>
                      <TableCell>{a.qty}</TableCell>
                      <TableCell>{fmt(a.unitPrice || a.price)}</TableCell>
                      <TableCell>{a.gstPercent || 0}%</TableCell>
                      <TableCell>
                        <Chip size='small' color={a.isActive ? 'success' : 'default'} label={a.isActive ? 'Yes' : 'Removed'} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(a.startDateTime)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {(data.foodOrders || []).length > 0 && (
            <Section title={`Restaurant / Food Orders charged to this room (${data.foodOrders.length})`}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {['Order Id', 'Table', 'Final', 'Paid', 'When'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.foodOrders.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>ORD_{f.id}</TableCell>
                      <TableCell>{f.tableno || '—'}</TableCell>
                      <TableCell>{fmt(f.finalPrice)}</TableCell>
                      <TableCell>
                        <Chip size='small' color={f.isPaid ? 'success' : 'default'} label={f.isPaid ? 'Paid' : 'Unpaid'} />
                        {f.isCancel && <Chip size='small' color='error' label='Cancelled' sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(f.createDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}
        </>
      )}
    </Box>
  );
}